import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Database, Ruler, Mail } from "lucide-react";
import { useSideTittel } from "@/hooks/useSideTittel";

const KJELDER = [
  ["SSB KOSTRA", "Rekneskap for kommunekassa og kommunekonsernet: inntekter, utgifter, gjeld, fond, premieavvik, arbeidskapital og investeringar"],
  ["SSB tenestestatistikk", "Barn per årsverk i barnehagen (13502), elevar og lærarårsverk (11971/11981) og årsverk per brukar i omsorgstenesta (12209)"],
  ["SSB befolkning og inntekt", "Folketal og framskriving, gjennomsnittsalder, hushaldsinntekt, låginntekt (EU60) og Gini-koeffisient"],
  ["SSB sosialhjelp", "Utbetalt sosialhjelp, talet på tilfelle og stønadstid (13995 og 14019)"],
  ["SIFO/OsloMet", "Referansebudsjett for forbruksutgifter, brukt som målestokk for livsopphald"],
  ["Arbeids- og inkluderingsdepartementet", "Statlege rettleiande satsar for økonomisk stønad (rundskriv A-1)"],
  ["Huseiernes bokostnadsindeks og SSB", "Kommunale gebyr, eigedomsskatt, bustadprisar og leigeprisar"],
  ["Wikipedia", "Ordførarar og styringsparti i kommunane"],
];

const METODE = [
  "Alle tala blir henta direkte frå dei offentlege API-ane, og kvar graf viser kva tabell tala kjem frå. Ingen tal er rekna om utan at det står kva som er gjort.",
  "Total gjeld er langsiktig gjeld utanom pensjonsforpliktingar pluss lån. Gjeldsandel er total gjeld delt på eigedelar. Driftsmargin er driftsresultat delt på driftsinntekter.",
  "Samanlikningane viser kommunen mot fylkessnittet, landssnittet, nabokommunane (kommunar med felles grense) og storbysnittet for Oslo, Bergen, Trondheim og Stavanger. Snitt er vekta der det er fagleg rett.",
  "Kommune- og fylkessamanslåingar er markerte med ei stipla linje i historikkgrafane, og tala er henta på den gamle koden før endringa, slik at utviklinga er samanliknbar bakover i tid.",
  "«Verst i landet»-listene rangerer alle kommunar med tal for det nyaste året. Du kan avgrense listene til eitt fylke for å samanlikne nabokommunar.",
  "Sosialhjelpstala blir sette opp mot SIFO-budsjettet og den statlege rettleiande satsen. Det finst ingen offisiell statistikk per kommune for kor mange som lever under SIFO-budsjettet, og kommunale minstesatsar finst ikkje i noko nasjonalt datasett.",
];

const OmSida = () => {
  const navigate = useNavigate();
  useSideTittel(
    "Om sida – kjelder og metode | Kommuneopprøret",
    "Kjelder, metode og kontaktinfo for Kommuneopprøret: kommuneøkonomi, bemanning, gjeld, gebyr, ulikskap og livsopphald basert på offentlege tal."
  );

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      <header className="bg-white border-b border-[#E6E9F0]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="om-tilbake">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-[#1F2A36]">Om sida</h1>
            <p className="text-sm text-[#8AA4BF]">Kjelder, metode og kontakt</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6" data-testid="om-side">
        <Card className="bg-white border border-[#E6E9F0] rounded-lg">
          <CardContent className="pt-6">
            <p className="text-[#1F2A36]">
              Kommuneopprøret samlar offentlege tal om kommuneøkonomi på éin stad, slik at innbyggjarar, tillitsvalde og
              lokalpolitikarar kan sjå kva som faktisk skjer i si eiga kommune – og samanlikne med nabokommunane, fylket
              og landet.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-[#E6E9F0] rounded-lg">
          <CardHeader className="border-b border-[#E6E9F0] pb-4">
            <CardTitle className="text-base font-medium text-[#1F2A36] flex items-center gap-2">
              <Database className="w-4 h-4 text-[#103766]" /> Kjelder
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4" data-testid="om-kjelder">
            {KJELDER.map(([namn, tekst]) => (
              <div key={namn}>
                <p className="text-sm font-medium text-[#1F2A36]">{namn}</p>
                <p className="text-sm text-[#4B647F]">{tekst}</p>
              </div>
            ))}
            <p className="text-xs text-[#8AA4BF] pt-2 border-t border-[#E6E9F0]">
              Tala blir henta automatisk frå{" "}
              <a href="https://data.ssb.no" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline">
                data.ssb.no
              </a>{" "}
              og oppdaterte når SSB publiserer nye tal.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-[#E6E9F0] rounded-lg">
          <CardHeader className="border-b border-[#E6E9F0] pb-4">
            <CardTitle className="text-base font-medium text-[#1F2A36] flex items-center gap-2">
              <Ruler className="w-4 h-4 text-[#103766]" /> Metode
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-3" data-testid="om-metode">
            {METODE.map((tekst) => (
              <p key={tekst.slice(0, 24)} className="text-sm text-[#4B647F]">
                {tekst}
              </p>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white border border-[#E6E9F0] rounded-lg">
          <CardHeader className="border-b border-[#E6E9F0] pb-4">
            <CardTitle className="text-base font-medium text-[#1F2A36] flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#103766]" /> Kontakt
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-2" data-testid="om-kontakt">
            <p className="text-sm text-[#4B647F]">
              Sida er laga av Per Arne Flatberg –{" "}
              <a href="mailto:paffen@gmail.com" className="text-[#E52437] hover:underline" data-testid="om-epost">
                paffen@gmail.com
              </a>
            </p>
            <p className="text-sm text-[#4B647F]">
              Finn du ein feil i tala, eller saknar du ein graf? Send ein e-post, så blir det retta. Del gjerne –
              tala er offentlege og skal brukast.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default OmSida;
